import React, { useEffect, useRef, useState } from "react";
import { RCCGrid, RCCIcon, RCCServicesApi, RCCDrawer, RCCRefDrawerProps, RCCButton, RCCTooltip, RCCAlert, RCCHookToast } from "react-common-components";
import ModalViewNotification from "src/components/ModalViewNotification";
import { IRefModalViewNotification } from "src/components/ModalViewNotification/interface";
import Title from "src/components/Title";
import { useAuth } from "src/context/Auth/context";
import { ISystemNotifications } from "src/context/Auth/interface";
import { useTranslation } from "src/context/Translations/context";
import useEffectSkipInitialRender from "src/hooks/useEffectSkipInitialRender";
import { standardErrorMessages } from "src/utils/errors";
import { v4 } from "uuid";
import SocketPaths, { INotificationResponse, ISocketReturnData, NotificationDTO } from "./interface";
import NotificationCard from "./notificationCard";
import { ButtonsContainer, HeaderNotifications, MessageListContainer } from "./styles";

const Notifications: React.FC = () => {
	const { addToast } = RCCHookToast();
	const { translate } = useTranslation();
	const { user, systemNotifications } = useAuth();
	const { REACT_APP_WEBSOCKETS } = process.env;
	
	const modalViewNotification = useRef<IRefModalViewNotification>(null);
	const modalViewNotificationAndMarkAsRead = useRef<IRefModalViewNotification>(null);
	const drawerRef = useRef<RCCRefDrawerProps>(null);
	let timerToLoadNotifications = 0; 

	const [notifications, setNotifications] = useState<NotificationDTO[]>([]);
	const [total, setTotal] = useState(1);
	const [unread, setUnread] = useState(0);
	const [errorLoading, setErrorLoading] = useState(false);
	const pageValue = useRef(1);
	const [showOnlyUnread, setShowOnlyUnread] = useState(false);
	const socket = useRef<WebSocket | undefined>(undefined);

	const setTimerToLoadNotifications = (funct: () => void) => {
		clearTimeout(timerToLoadNotifications);
		timerToLoadNotifications = window.setTimeout(() => {
			funct();
		}, 5000);
	};

	// PING
	// const intervalIdToPingWebsocket = useRef(0);
	// const socketPing = () => {
	// 	if(!socket.current)
	// 		return;
	// 	socket.current.send("ping");
	// };
	// const setPing = () => {
	// 	intervalIdToPingWebsocket.current = window.setInterval(socketPing, 5000);
	// };
	// const clearPing = () => {
	// 	clearInterval(intervalIdToPingWebsocket.current);
	// };

	const intervalIdToFlashNotifications = useRef(0);
	const [showNotificationsOnTab, setShowNotificationsOnTab] = useState(false);

	const closeDrawer = () => {
		drawerRef.current?.closeDrawer();
		clearInterval(intervalIdToFlashNotifications.current);
		window.document.title = "Scoreplan";
		setShowNotificationsOnTab(false);
	};

	const flashNotifications = (showTitle?: boolean) => {
		window.document.title = showTitle || unread === 0 ? "Scoreplan" : `(${unread}) ${translate("Nova(s) notificação(ões)")} | Scoreplan`;
	};

	const loopFlashNotifications = () => {
		let showTitle = false;
		intervalIdToFlashNotifications.current = window.setInterval(() => {
			flashNotifications(showTitle);
			showTitle = !showTitle;
		}, 1250);
	};

	useEffectSkipInitialRender(() => {
		clearInterval(intervalIdToFlashNotifications.current);
		if(!showNotificationsOnTab){
			window.document.title = "Scoreplan";
			return;
		}
		loopFlashNotifications();
	}, [unread, showNotificationsOnTab]);

	const loadWebSocket = () => {
		socket.current = new WebSocket(`${REACT_APP_WEBSOCKETS}/websockets/notifications?efa005Id=${user.efa005Id}`);
		// Descomentar os eventos abaixo para debug de qnd conecta e desconecta o socket
		// socket.current.onopen = (event: any) => {
		// 	// LOAD NOTIFICATIONS
		// 	console.log(event);
		// };
	
		// socket.current.onclose = (event: any) => {
		// 	console.log(event);
		// };
	
		socket.current.onmessage = (event: any) => {
			let data;
			try{
				data = JSON.parse(event.data);
			}
			catch(err){ 
				return;
			}
			socketOnMessage(data);		
		};

		// setPing();
	};
	const socketOnMessage = (dt: ISocketReturnData) => {
		switch(dt.path){
		case SocketPaths.LOAD_NOTIFICATIONS: {
			const efa005Ids: number[] = dt.data;
			if(!efa005Ids.includes(user.efa005Id))
				return;
			setTimerToLoadNotifications(() => {
				addToast({
					title: translate("Nova(s) notificação(ões)"),
					description: translate("Você tem nova(s) notificação(ões)"),
					color: "blue",
					icon: { name: "bell" }
				});
				pageValue.current = 1;
				loadNotifications(true, 1);
				setShowNotificationsOnTab(true);
			});
			break;
		}
		default: {
			// console.log(dt);
			break;
		}
		}
	};
	const socketClose = () => {
		if(!socket.current)
			return;
		clearInterval(intervalIdToFlashNotifications.current);
		window.document.title = "Scoreplan";
		// clearPing();
		socket.current.close();
	};
	// const socketSendMessage = (message: string) => {
	// 	if(!socket.current)
	// 		return;

	// 	socket.current.send(message);
	// };
	
	const loadNotifications = async(clear?: boolean, page?: number) => {
		try{
			setErrorLoading(false);
			const {data} : {data: INotificationResponse} = await RCCServicesApi.getRequest({
				method: "Notifications/ListMyNotifications",
				urlParameters: {
					Page: page ?? pageValue.current,
					ElementsPerPage: 20,
					ShowOnlyUnread: showOnlyUnread
				}
			});
			pageValue.current += 1;
			setNotifications((prevState) => clear ? data.notifications : [...prevState, ...data.notifications]);
			setTotal(data.total);
			setUnread(data.unread);
		}catch(err){
			setErrorLoading(true);
		}
	};

	const markAllAsRead = async () => {
		const oldState = {
			notifications: [...notifications],
			unread
		};
		setNotifications((prevState) => prevState.map((pState) => ({
			...pState,
			isRead: true
		})));
		setUnread(0);
		try{
			await RCCServicesApi.postRequest({
				method: "Notifications/MarkAllAsRead"
			});
		}catch(err){
			standardErrorMessages(err, translate("Erro ao marcar todas notificações como lida"), addToast);
			setNotifications(oldState.notifications);
			setUnread(oldState.unread);
		}
	};
	const toggleRead = async (id: number, isRead: boolean, isSystem: boolean, index: number) => {
		const oldState = {
			notifications: [...notifications],
			unread
		};
		setNotifications((prevState) => {
			if(showOnlyUnread && isRead)
				return prevState.filter((x) => x.id !== id);
				
			const updateState = [...prevState];
			updateState[index].isRead = isRead;
			return updateState;
		});
		setUnread((prevState) => isRead ? prevState - 1 : prevState + 1);

		if(id === 1 || id === 2 || id === 3)
			return;

		try{
			await RCCServicesApi.postRequest({
				method: "Notifications/MarkAsRead",
				urlParameters: {
					id,
					isRead,
					isSystem
				}
			});
		}catch(err){
			standardErrorMessages(err, isRead ? translate("Erro ao marcar notificação como lida") : translate("Erro ao marcar notificação como não lida"), addToast);
			setNotifications(oldState.notifications);
			setUnread(oldState.unread);
		}
	};

	const openModalSystemNotifications = (notificationsOfSystem: ISystemNotifications[]) => {
		if(notificationsOfSystem === null || (Array.isArray(notificationsOfSystem) && notificationsOfSystem.length === 0))
			return;
		const firstNotification = notificationsOfSystem[0];
		notificationsOfSystem.shift();
		modalViewNotificationAndMarkAsRead.current?.openModalNotification(
			firstNotification.efn001Tipo,
			firstNotification.efn001Msgsimples,
			firstNotification.efn001Msgcompleta,
			firstNotification.efn001Id,
			() => {
				window.setTimeout(() => {
					openModalSystemNotifications(notificationsOfSystem);
				}, 500);
			}
		);
	};


	useEffectSkipInitialRender(() => {
		loadNotifications(true);
	}, [showOnlyUnread]);

	useEffect(() => {
		loadWebSocket();
		loadNotifications();
		window.setTimeout(() => {
			openModalSystemNotifications(systemNotifications);
		}, 1000);
		return () => {
			socketClose();
		};
	}, []);

	return <>
		<RCCTooltip
			color="main"
			text={translate("Notificações")}
			position="bottom"
			styleInline="line-height: 1rem;"
		>
			<button type="button" className={`buttons-toggle ${unread > 0 ? "animate-ringing" : ""}`} onClick={() => drawerRef.current?.openDrawer()}>
				<RCCIcon name="bell" size={22}/>
				{unread > 0 && <div className="notifications-number" style={unread > 99 ? { width: "18px", borderRadius: "10px" } : {}}>
					{unread > 99 ? "+99" : unread}
				</div>}
			</button>
		</RCCTooltip>
		
		<RCCDrawer
			ref={drawerRef} 
			anchor="right" 
			widthDrawer="28rem"
			onClose={closeDrawer}
			styleInline={`>div.MuiPaper-root{ 
				background-color: var(--input-background-color);
				padding: 1rem; 
				border: 1px solid var(--border-color); 

				>div.presentation{ 
					height: 100%; 
				} 

				@media(max-width: 420px){
					width: 300px;
					>div.presentation{ 
						width: 100%;
					}
				}
			}`}
		>
			{errorLoading ? <RCCAlert 
				title={translate("Erro")}
				description={translate("Erro ao buscar as notificações. Atualize a página ou tente novamente mais tarde")}
			/>: 
				<RCCGrid
					container
					item
					direction="column"
					style={{height: "100%"}}
				>
					<HeaderNotifications>
						<Title color="purple" size="lg" bold>
							<RCCIcon 
								color="var(--purple)"
								name="bell"
								style={{marginRight: "4px"}}
							/>
							{translate("Notificações")}
						</Title>				
						<RCCIcon name="close" size={26}  color="var(--close-icon)" onClick={closeDrawer}/>
					</HeaderNotifications>
					<RCCGrid container item justifyContent="space-between" alignItems="center" styleInline="border-bottom: 1px solid var(--border-color); margin-bottom: 4px !important;">
						<div style={{ display: "flex" }}>
							<RCCButton
								size="md"
								onClick={() => {
									pageValue.current = 1;
									setShowOnlyUnread(false);
								}}
								style={{background: "var(--accordion-color)", boxShadow: "none", color: "var(--purple)", fontWeight: !showOnlyUnread ? "bold" : "normal"}}
							>
								{translate("Tudo")}
							</RCCButton>
							<RCCButton
								size="md"
								onClick={() => {
									pageValue.current = 1;
									setShowOnlyUnread(true);
								}}
								style={{background: "var(--accordion-color)", boxShadow: "none", color: "var(--purple)", fontWeight: showOnlyUnread ? "bold" : "normal"}}
							>
								{translate("Não lidas")}
							</RCCButton>
						</div>
						<RCCTooltip
							text={translate("Marcar todas como lidas")}
							color="purple"
							position="leftEnd"
						>
							<RCCButton
								size="md"
								icon={{
									name: "mailOpen",
									color: "var(--purple)"
								}}
								style={{background: "var(--accordion-color)", boxShadow: "none"}}
								onClick={markAllAsRead}
							/>
						</RCCTooltip>
					</RCCGrid>
					<MessageListContainer>
						{total === 0 || (showOnlyUnread && unread === 0) ? <>
							<RCCAlert 
								title={translate("Isso é tudo")}
								description={translate("Você viu todas as suas notificações")}
							/>
						</> : <>
							{notifications.length > 0 && notifications.map((notification, index: number) => (
								<NotificationCard 
									key={v4()} 
									notification={notification} 
									toggleRead={(id: number, isRead: boolean, isSystem: boolean) => toggleRead(id, isRead, isSystem, index)}
									closeDrawer={closeDrawer}
									openSystemNotification={(notificationDTO: NotificationDTO) => {
										modalViewNotification.current?.openModalNotification(
											notificationDTO.type,
											notificationDTO.message,
											notificationDTO.fullMessage,
											notificationDTO.id
										);
									}}
								/>
							))}
							{total > notifications.length && <>
								<hr />
								<ButtonsContainer onClick={() => loadNotifications()}>
									{translate("Mostrar mais")}
								</ButtonsContainer>
							</>}
						</>}
					</MessageListContainer>
				</RCCGrid>
			}
		</RCCDrawer>
		
		<ModalViewNotification 
			ref={modalViewNotificationAndMarkAsRead} 
			onSubmit={(id) => {
				RCCServicesApi.postRequest({
					method: "Notifications/MarkAsRead",
					urlParameters: {
						id,
						isRead: true,
						isSystem: true
					}
				});
				setNotifications((prevState) => prevState.map((pState) => {
					if(pState.id === id)
						return {
							...pState,
							isRead: true
						};
					return pState;
				}));
				setUnread((prevState) => prevState - 1);
			}}
		/>
		<ModalViewNotification 
			ref={modalViewNotification}
		/>
	</>;
};

export default Notifications;
